import type {ArticleCardLayoutRecord, NodeChunk, StateAware} from '../../types/types.ts';
import type {ItemState} from './item-state.ts';

export type Asset = {
	url?: string;
	thumbnailUrl?: string;
	pictureHtml?: string;
	captionHtml?: string;
};

export type ArticleData = {
	id: string;
	objectId: string | null;
	data: object & {
		category?: string;
		articletype?: string;
	};
	cardLayouts?: {
		desktop: ArticleCardLayoutRecord;
		mobile: ArticleCardLayoutRecord;
	};
	publishedAt: string;
	sort?: number;
	sourceId?: string;
	groupBy?: string;
	section?: string;
	story?: string;
	contentGroups?: NodeChunk[];
	layout: {
		html: string;
		screenshot?: string;
		uuid?: string;
		version?: number;
	};
	relatedArticles?: RelatedArticle[];
	sourceUrl?: string;
	pretitle: string;
	title: string;
	tags?: Tag[];
	intro: string;
	size: number;
	asset?: Asset;
	byline?: {
		html?: string;
		items: BylineData[];
	};
};

export type Tag = {
	displayName: string;
	urlPattern: string;
};

export type PrintArticleData = ArticleData & {
	pages: number[];
	displayPages?: number[];
};

export type RelatedArticle = {
	id: string;
	assetUrl: string;
	title: string;
	intro: string;
	publishedAt: string;
	permalink?: string;
};

export type BylineData = {
	author?: string;
	name?: string;
	bio?: string;
	email?: string;
	label?: string;
	photoUrl?: string;
};

export class Article implements StateAware {
	readonly #state;
	readonly #data;
	isArticleGroup = false;
	sourceId?: string;
	groupedArticles: Article[] = [];
	relatedArticles: RelatedArticle[] = [];
	contentGroups?: NodeChunk[];
	cardLayouts?: {
		desktop: ArticleCardLayoutRecord;
		mobile: ArticleCardLayoutRecord;
	};

	constructor(data: ArticleData, state: ItemState) {
		this.contentGroups = data.contentGroups;
		this.#data = data;
		this.#state = state;
		this.cardLayouts = data.cardLayouts;
		this.sourceId = data.sourceId;

		// Enrich related articles with a permalink
		this.relatedArticles = (data.relatedArticles || []).map((related) => {
			related.permalink = this.createPermalink(related.id);
			return related;
		});
	}

	getState() {
		return this.#state;
	}

	get state() {
		return this.#state.state;
	}

	get starred() {
		return this.#state.starred;
	}

	get mobileProgress() {
		return this.#state.mobileProgress;
	}

	get desktopProgress() {
		return this.#state.desktopProgress;
	}

	get groupBy() {
		if (this.#data.groupBy) {
			return this.#data.groupBy;
		}

		return '';
	}

	set groupBy(value: string) {
		this.#data.groupBy = value;
	}

	get sortOrder() {
		return this.#data.sort || -1;
	}

	set sortOrder(value: number) {
		this.#data.sort = value;
	}

	get section() {
		return this.#data.section || '';
	}

	get story() {
		return this.#data.story || '';
	}

	get data() {
		return this.#data;
	}

	get id() {
		if (this.isArticleGroup && this.groupedArticles.length) {
			return [this.#data.id, ...this.groupedArticles.map((article: Article): string => article.id)].join('-');
		}
		return this.#data.id;
	}

	get intro() {
		return this.#data.intro;
	}

	get pretitle() {
		return this.#data.pretitle;
	}

	get title() {
		if (this.isArticleGroup && this.groupedArticles.length) {
			return '';
		}
		return this.#data.title;
	}

	get permalink() {
		return this.createPermalink(this.id);
	}

	createPermalink(id: string) {
		if (this.sourceId) {
			return `/article/${this.sourceId}/${id}`;
		}

		return `/article/${id}`;
	}

	get listTitle() {
		if (this.isArticleGroup && this.groupedArticles.length) {
			return `-- Notiser (${this.groupedArticles.length + 1}) --`;
		}

		return this.title;
	}

	get asset() {
		return this.#data.asset;
	}

	get byline() {
		return this.#data.byline;
	}

	get tags() {
		return this.#data.tags;
	}

	get category() {
		return this.#data.data.category;
	}

	get articletype() {
		return this.#data.data.articletype;
	}

	get publishedAt() {
		return this.#data.publishedAt;
	}

	get sourceUrl() {
		return this.#data.sourceUrl || '';
	}

	set sourceUrl(value: string) {
		this.#data.sourceUrl = value;
	}

	get layout() {
		return this.#data.layout;
	}

	set title(title: string) {
		this.data.title = title;
	}

	set intro(intro: string) {
		this.data.intro = intro;
	}

	set story(story: string) {
		this.data.story = story;
	}

	set markup(html: string) {
		this.data.layout.html = html;
	}

	get markup() {
		if (this.isArticleGroup && this.groupedArticles.length) {
			return [this.#data.layout.html, ...this.groupedArticles.map((article): string => article.markup)].join(
				'\n'
			);
		}

		return this.#data.layout.html;
	}

	get size() {
		if (this.isArticleGroup && this.groupedArticles.length) {
			return this.groupedArticles.reduce((acc, {size}): number => acc + size, 0);
		}

		return this.#data.size;
	}

	set size(value) {
		this.#data.size = value;
	}

	addToGroup(article: Article) {
		this.groupedArticles.push(article);
	}

	makeGroup() {
		this.isArticleGroup = true;
	}
}
